// Concrete browser implementations of the platform seams the call engine (J8)
// and its media helpers declare as injected dependencies. Everything that
// touches a browser global — WebSocket, RTCPeerConnection, getUserMedia, Web
// Audio, IndexedDB — lives here, behind the same structural interfaces the
// engine's unit tests fake. Nothing else in the shell reaches for a DOM API
// directly.

import type { IceCandidate } from '@merkury/protocol';
import { ENDPOINTS_STATE_KEY } from '@merkury/brain/storage/kvKeys.js';
import {
  MIC_CONSTRAINTS,
  type EndpointListStorage,
  type KeyPinStorage,
  type MediaAdapter,
  type MediaControlDeps,
  type MediaDeviceInfoLike,
  type MediaStreamLike,
  type PeerConfig,
  type PeerConnectionFactory,
  type PeerConnectionLike,
  type WebSocketFactory,
  type WebSocketLike,
} from '@merkury/brain/call/index.js';
import type { EchoRecorderLike, EchoRecording, EchoTestDeps } from '@merkury/brain/call/media/echoTest.js';
import type {
  AudioTrackLike,
  RtpReceiverLike,
  RtpSenderLike,
  SessionDescriptionLike,
} from '@merkury/brain/call/rtc/peer.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';

// --- Signaling transport ------------------------------------------------------

/** `new WebSocket(url)` behind the engine's minimal `WebSocketLike` surface. */
export const browserWebSocketFactory: WebSocketFactory = (url) => {
  const ws = new WebSocket(url);
  const like: WebSocketLike = {
    get readyState() {
      return ws.readyState;
    },
    send: (data) => ws.send(data),
    close: (code, reason) => ws.close(code, reason),
    onopen: null,
    onclose: null,
    onerror: null,
    onmessage: null,
  };
  ws.onopen = (ev) => like.onopen?.(ev);
  ws.onclose = (ev) => like.onclose?.({ code: ev.code, reason: ev.reason });
  ws.onerror = (ev) => like.onerror?.(ev);
  ws.onmessage = (ev) => like.onmessage?.({ data: ev.data });
  return like;
};

// --- WebRTC peer --------------------------------------------------------------

/** Normalize a browser ICE candidate to the opaque wire shape (docs/02 §4). */
function toWireCandidate(candidate: RTCIceCandidate): IceCandidate {
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
  };
}

/**
 * `new RTCPeerConnection` behind `PeerConnectionLike`. The one non-trivial
 * translation is `getStats`: a browser `RTCStatsReport` iterates as
 * `[id, entry]` pairs, but the quality monitor wants the entries themselves.
 */
export const browserPeerFactory: PeerConnectionFactory = (config: PeerConfig) => {
  const pc = new RTCPeerConnection({
    iceServers: config.iceServers,
    iceTransportPolicy: config.iceTransportPolicy,
  });
  const like: PeerConnectionLike = {
    get signalingState() {
      return pc.signalingState;
    },
    get iceConnectionState() {
      return pc.iceConnectionState;
    },
    get localDescription() {
      return pc.localDescription;
    },
    onicecandidate: null,
    oniceconnectionstatechange: null,
    ontrack: null,
    createOffer: (opts) => pc.createOffer(opts) as Promise<SessionDescriptionLike>,
    createAnswer: () => pc.createAnswer() as Promise<SessionDescriptionLike>,
    setLocalDescription: (desc) => pc.setLocalDescription(desc as RTCLocalSessionDescriptionInit),
    setRemoteDescription: (desc) => pc.setRemoteDescription(desc as RTCSessionDescriptionInit),
    addIceCandidate: (candidate) => pc.addIceCandidate(candidate as RTCIceCandidateInit),
    restartIce: () => pc.restartIce(),
    setConfiguration: (cfg) =>
      pc.setConfiguration({
        iceServers: cfg.iceServers,
        iceTransportPolicy: cfg.iceTransportPolicy,
      }),
    close: () => pc.close(),
    getStats: async () => [...(await pc.getStats()).values()],
    addTrack: (track, stream) =>
      pc.addTrack(track as MediaStreamTrack, stream as unknown as MediaStream),
    getSenders: () => pc.getSenders() as unknown as RtpSenderLike[],
    getReceivers: () => pc.getReceivers() as unknown as RtpReceiverLike[],
  };
  pc.onicecandidate = (ev) =>
    like.onicecandidate?.({ candidate: ev.candidate ? toWireCandidate(ev.candidate) : null });
  pc.oniceconnectionstatechange = () => like.oniceconnectionstatechange?.();
  // Without this forward the PeerHandle's `pc.ontrack = …` lands on the
  // wrapper and the REAL connection never reports remote media — every call
  // connects but stays silent both ways (found on-device; the loopback tests'
  // mock pc reads .ontrack directly, so they can't catch it).
  pc.ontrack = (ev) =>
    like.ontrack?.({
      ...(ev.track ? { track: ev.track as unknown as AudioTrackLike } : {}),
      streams: ev.streams as unknown as readonly MediaStreamLike[],
    });
  return like;
};

// --- Mic capture for the engine ----------------------------------------------

/** The engine's mic source: constraints already baked in (docs/01 §4.2). */
export const browserMediaAdapter: MediaAdapter = {
  getLocalAudio: () =>
    navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS }) as Promise<MediaStreamLike>,
};

// --- MediaControl + EchoTest platform deps -----------------------------------

/** A single hidden <audio> element playing the remote track (docs/06 §3). */
let remoteAudioEl: HTMLAudioElement | undefined;
function remoteAudio(): HTMLAudioElement {
  if (!remoteAudioEl) {
    remoteAudioEl = document.createElement('audio');
    remoteAudioEl.autoplay = true;
    remoteAudioEl.hidden = true;
    document.body.appendChild(remoteAudioEl);
  }
  return remoteAudioEl;
}

/**
 * Keep the sink's srcObject in step with the engine's published remoteStream
 * (set on connect, replaced on renegotiation, null on call end). Playback
 * itself is gated by the iOS gesture rule — MediaControl.unlockAudio() calls
 * sink.play() from the accept tap. Returns the unsubscribe.
 */
export function attachRemoteAudio(state: {
  get(): { remoteStream: unknown };
  subscribe(fn: () => void): () => void;
}): () => void {
  const el = remoteAudio();
  const sync = () => {
    const s = (state.get().remoteStream ?? null) as MediaStream | null;
    if (el.srcObject !== s) {
      el.srcObject = s;
      // autoplay isn't re-triggered reliably once the element has been paused
      // (e.g. by a previous call ending); kick playback explicitly. Autoplay
      // policy allows it: the WebView disables the gesture requirement and on
      // the web an active mic capture exempts us.
      if (s) void el.play().catch(() => undefined);
    }
  };
  const unsub = state.subscribe(sync);
  sync();
  return unsub;
}

/** A lazily-created AudioContext shared by the media helpers. */
let sharedCtx: AudioContext | undefined;
function audioContext(): AudioContext {
  sharedCtx ??= new AudioContext();
  return sharedCtx;
}

function toDeviceInfo(d: MediaDeviceInfo): MediaDeviceInfoLike {
  return { deviceId: d.deviceId, kind: d.kind, label: d.label };
}

/** Browser `MediaControlDeps` — the `browserMediaDeps()` control.ts references. */
export function browserMediaControlDeps(): MediaControlDeps {
  const el = remoteAudio();
  return {
    getUserMedia: (constraints) =>
      navigator.mediaDevices.getUserMedia(
        constraints as MediaStreamConstraints,
      ) as Promise<MediaStreamLike>,
    enumerateDevices: async () =>
      (await navigator.mediaDevices.enumerateDevices()).map(toDeviceInfo),
    resumeAudioContext: () => audioContext().resume(),
    sink: {
      play: () => el.play(),
      pause: () => el.pause(),
      setSinkId: (deviceId) =>
        // Chrome/Edge only; typed loosely because setSinkId isn't in every lib.dom.
        (el as HTMLAudioElement & { setSinkId?(id: string): Promise<void> }).setSinkId?.(deviceId) ??
        Promise.resolve(),
      get srcObject() {
        return el.srcObject;
      },
      set srcObject(value: unknown) {
        el.srcObject = value as MediaStream | null;
      },
    },
  };
}

/** RMS level 0..1 from an AnalyserNode's time-domain data. */
function rmsLevel(analyser: AnalyserNode, buffer: Float32Array<ArrayBuffer>): number {
  analyser.getFloatTimeDomainData(buffer);
  let sum = 0;
  for (const sample of buffer) sum += sample * sample;
  return Math.min(1, Math.sqrt(sum / buffer.length) * 4);
}

/** Browser `EchoTestDeps` (docs/06 §3): record ~5 s, then play it back metered. */
export function browserEchoTestDeps(): EchoTestDeps {
  return {
    getUserMedia: (constraints) =>
      navigator.mediaDevices.getUserMedia(
        constraints as MediaStreamConstraints,
      ) as Promise<MediaStreamLike>,
    startRecorder: (stream): Promise<EchoRecorderLike> => {
      const ctx = audioContext();
      const source = ctx.createMediaStreamSource(stream as unknown as MediaStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);
      const recorder = new MediaRecorder(stream as unknown as MediaStream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunks.push(ev.data);
      };
      recorder.start();
      return Promise.resolve({
        readLevel: () => rmsLevel(analyser, buffer),
        stop: () =>
          new Promise<EchoRecording>((resolve) => {
            recorder.onstop = () => {
              source.disconnect();
              resolve(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
            };
            recorder.stop();
          }),
      });
    },
    play: async (recording, onLevel) => {
      const ctx = audioContext();
      const data = await (recording as Blob).arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(data);
      const src = ctx.createBufferSource();
      src.buffer = audioBuffer;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      const buffer = new Float32Array(analyser.fftSize);
      src.connect(analyser);
      analyser.connect(ctx.destination);
      await new Promise<void>((resolve) => {
        const meter = setInterval(() => onLevel(rmsLevel(analyser, buffer)), 100);
        src.onended = () => {
          clearInterval(meter);
          src.disconnect();
          analyser.disconnect();
          resolve();
        };
        src.start();
      });
    },
  };
}

// --- Persistent storage seams -------------------------------------------------

/** TOFU key-pin slice (docs/03 §4.2) over the app's IndexedDB KV. */
export function kvKeyPinStorage(kv: KvStore): KeyPinStorage {
  const key = (userId: string): string => `pin:${userId}`;
  return {
    get: (userId) => kv.get<string>(key(userId)).then((v) => v ?? null),
    set: (userId, publicKeyB64) => kv.put(key(userId), publicKeyB64),
    delete: (userId) => kv.delete(key(userId)),
  };
}

/** Persisted endpoint-list state (last verified list + last-known-good URL). */
export function kvEndpointListStorage(kv: KvStore): EndpointListStorage {
  return {
    load: () =>
      kv
        .get<Awaited<ReturnType<EndpointListStorage['load']>>>(ENDPOINTS_STATE_KEY)
        .then((v) => v ?? null),
    save: (state) => kv.put(ENDPOINTS_STATE_KEY, state),
  };
}
